import * as THREE from 'three';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';

// bvh — make raycasting a room affordable.
//
// FloorClamp fires one ray straight down every frame to decide where you may
// stand. Against a room that is one big mesh set, three.js tests EVERY triangle
// whose bounding volume the ray enters — and standing inside the subway's train
// puts the ray origin inside that train's volume, so nothing can be rejected and
// it walks ~130k triangles per frame. That showed up as
// "'requestAnimationFrame' handler took 63ms" while walking the carriage: not a
// slowdown, a stutter, which is a much harder thing to recognise from inside.
//
// A BVH turns that from O(triangles) into O(log n). Built once per room, so the
// cost lands during load rather than during play.
//
// ── PATCHING THE PROTOTYPE IS THE INTENDED USAGE ─────────────────────────────
// `acceleratedRaycast` checks for `geometry.boundsTree` and falls straight
// through to three.js's own implementation when there isn't one. So this is
// inert for every mesh we do not explicitly build a tree for — pointer events,
// placements, NPC picking all behave exactly as before.
let patched = false;
export function enableBVH() {
  if (patched) return;
  THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
  THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
  THREE.Mesh.prototype.raycast = acceleratedRaycast;
  patched = true;
}

// Build trees for everything under `root`. Returns how many were built, so the
// caller can say so — a silent optimisation that fails is worse than none,
// because the symptom (an occasional long frame) is easy to blame on anything.
export function buildBVH(root) {
  enableBVH();
  let built = 0;
  let tris = 0;
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry || o.geometry.boundsTree) return;
    const idx = o.geometry.getIndex();
    const count = idx ? idx.count / 3 : (o.geometry.getAttribute('position')?.count || 0) / 3;
    // Below a few thousand triangles the linear scan is already cheap and the
    // tree costs more to build than it saves.
    if (count < 2000) return;
    o.geometry.computeBoundsTree();
    built += 1;
    tris += count;
  });
  return { built, tris: Math.round(tris) };
}

export function disposeBVH(root) {
  root?.traverse?.((o) => { if (o.isMesh && o.geometry?.boundsTree) o.geometry.disposeBoundsTree(); });
}
